import EndpointProps from "interface/EndpointProps"
import { fetchApiData } from "../ApiHelper"
import { get, post } from "../ApiClient"
import { ReportCategory } from "interface/ReportCategory"
import { CategoryTopic } from "interface/CategoryTopic";
import FetchDataByIDProps from "interface/FetchDataByIDProps";
import ZeroneReport from "interface/ZeroneReport";
import { DefaultApiResponse } from "../models/response/DefaultApiResponse";
import ReportAccess from "interface/ReportAccess";
import ReportCatalog from "interface/ReportCatalog";
import StripePaymentIntentResult from "interface/StripePaymentIntentResult";
import ReportPurchaseDefaultApiResponse from "interface/ReportPurchaseDefaultApiResponse";
import LegacyChart from "interface/LegacyChart";
import SimilarReport from "interface/SimilarReport";
import { ReportFilterOptions } from "interface/ReportFilterOptions";

/**
 * Fetches a list of report categories from the server.
 * 
 * This function performs an API call to retrieve report categories using the provided tag and
 * processes the API response using the `fetchApiData` utility.
 * 
 * @async
 * @function getReportCategories
 * @param {EndpointProps} params - The request parameters containing a unique tag for identifying the request.
 * @returns {Promise<{ data: ReportCategory[]; error: string | null }>} - A promise that resolves to an object containing the array of `ReportCategory` and any error message.
 * 
 * @example
 * // Example usage:
 * async function loadReportCategories() {
 *   const result = await getReportCategories({ tag: 'ReportFetch' });
 *   if (result.error) {
 *     console.error('Error fetching report categories:', result.error);
 *   } else {
 *     console.log('Fetched report categories:', result.data);
 *   }
 * }
 * // Sample result structure:
 * {
 *   data: [
 *       {
 *           id: "category1",
 *           title: "Market Analysis",
 *           meta: {
 *               icon: "mdi:chart-line",
 *               primaryColor: "#123456",
 *               secondaryColor: "#abcdef"
 *           },
 *           description: "In-depth market analysis across sectors.",
 *           topics: [
 *               { id: "topic1", title: "Consumer Trends", year: "2024" },
 *               { id: "topic2", title: "Market Share", year: "2023" }
 *           ]
 *       },
 *       {
 *           id: "category2",
 *           title: "Technology Innovations",
 *           meta: {
 *               icon: "mdi:lightbulb-on",
 *               primaryColor: "#654321",
 *               secondaryColor: "#fedcba"
 *           },
 *           description: "Discover the latest technology innovations.",
 *           topics: [
 *               { id: "topic3", title: "AI in Healthcare", year: "2024" },
 *               { id: "topic4", title: "Blockchain Applications", year: "2023" }
 *           ]
 *       }
 *   ],
 *   error: null
 * }
 */
export async function getReportCategories({ tag }: EndpointProps): Promise<{ success: boolean; data?: ReportCategory[]; error?: string | null }> {
    const result = await fetchApiData<DefaultApiResponse<ReportCategory[]>>(
        tag,
        () => get<DefaultApiResponse<ReportCategory[]>>({ tag, url: "/report/categories" }),
        (responseData) => responseData.data ?? []
    );

    return {
        success: result.response.status === "success",
        data: result.response.status === "success" ? result.response.data : [],
        error: result.response.status === "error" ? result.response.error ?? null : null,
    };
}

/**
 * Fetches a specific report category by ID from the server.
 * 
 * This function performs an API call to retrieve a report category based on its unique ID and
 * processes the API response using the `fetchApiData` utility.
 * 
 * @async
 * @function getReportCategoryById
 * @param {FetchDataByIDProps} params - The request parameters containing a tag and the report category's unique ID.
 * @returns {Promise<{ data: ReportCategory | null; error: string | null }>} - A promise that resolves to an object containing the report category data or an error message.
 * 
 * @example
 * // Example usage:
 * async function loadReportCategoryById() {
 *   const result = await getReportCategoryById({ tag: 'ReportCategoryFetch', id: '12345' });
 *   if (result.error) {
 *     console.error('Error fetching report category:', result.error);
 *   } else {
 *     console.log('Fetched report category:', result.data);
 *   }
 * }
 */
export async function getReportCategoryById({ tag, id }: FetchDataByIDProps): Promise<{ success: boolean; data?: ReportCategory | null; error?: string | null }> {
    const result = await fetchApiData<DefaultApiResponse<ReportCategory | null>>(
        tag,
        () => get<DefaultApiResponse<ReportCategory | null>>({ tag, url: `/report/categories/${id}` }),
        (responseData) => responseData.data ?? null
    );

    return {
        success: result.response.status === "success",
        data: result.response.status === "success" ? result.response.data : null,
        error: result.response.status === "error" ? result.response.error ?? null : null,
    };
}

/**
 * Fetches filter options for a specific report category by ID from the server.
 * 
 * This function performs an API call to retrieve filter options for a report category based on its unique ID and
 * processes the API response using the `fetchApiData` utility.
 * 
 * @async
 * @function getReportCategoryFilters
 * @param {FetchDataByIDProps} params - The request parameters containing a tag and the report category's unique ID.
 * @returns {Promise<{ data: ReportFilterOptions | null; error: string | null }>} - A promise that resolves to an object containing the filter options or an error message.
 * 
 * @example
 * // Example usage:
 * async function loadReportCategoryFilters() {
 *   const result = await getReportCategoryFilters({ tag: 'ReportCategoryFiltersFetch', id: '12345' });
 *   if (result.error) {
 *     console.error('Error fetching report category filters:', result.error);
 *   } else {
 *     console.log('Fetched report category filters:', result.data);
 *   }
 * }
 */
export async function getReportCategoryFilters({ tag, id}: FetchDataByIDProps): Promise<{ success: boolean; data?: ReportFilterOptions | null; error?: string | null }> {
    const result = await fetchApiData<DefaultApiResponse<ReportFilterOptions | null>>(
        tag,
        () => get<DefaultApiResponse<ReportFilterOptions | null>>({ tag, url: `/report/categories/${id}/filters` }),
        (responseData) => responseData.data ?? null
    );

    return {
        success: result.response.status === "success",
        data: result.response.status === "success" ? result.response.data : null,
        error: result.response.status === "error" ? result.response.error ?? null : null,
    };
}

/**
 * Fetches a list of top hot reports from the server.
 * 
 * This function performs an API call to retrieve top hot reports using the provided tag and
 * processes the API response using the `fetchApiData` utility.
 * 
 * @async
 * @function getTopHotReports
 * @param {EndpointProps} params - The request parameters containing a unique tag for identifying the request.
 * @returns {Promise<{ data: CategoryTopic[]; error: string | null }>} - A promise that resolves to an object containing the array of `CategoryTopic` and any error message.
 * 
 * @example
 * // Example usage:
 * async function loadTopHotReports() {
 *   const result = await getTopHotReports({ tag: 'TrendingReportsFetch' });
 *   if (result.error) {
 *     console.error('Error fetching top hot reports:', result.error);
 *   } else {
 *     console.log('Fetched top hot reports:', result.data);
 *   }
 * }
 * // Sample result structure:
 * {
 *   data: [
 *       {
 *           id: "report1",
 *           title: "2024 Elections in Ghana",
 *           description: "A comprehensive analysis of the upcoming elections.",
 *           dataset: "Politics",
 *           year: "2024",
 *           meta: {
 *               cover: "https://example.com/image.jpg"
 *           }
 *       },
 *       {
 *           id: "report2",
 *           title: "Beverage Consumption Trends",
 *           description: "Trends in beverage consumption in 2024.",
 *           dataset: "Drinks & Beverages",
 *           year: "2024",
 *           meta: {
 *               cover: "https://example.com/beverages.jpg"
 *           }
 *       }
 *   ],
 *   error: null
 * }
 */
export async function getTopHotReports({ tag }: EndpointProps): Promise<{ success: boolean; data?: CategoryTopic[]; error?: string | null }> {
    const result = await fetchApiData<DefaultApiResponse<CategoryTopic[]>>(
        tag,
        () => get<DefaultApiResponse<CategoryTopic[]>>({ tag, url: "/report?options=high-engagement" }),
        (response) => response.data ?? []
    );

    return {
        success: result.response.status === "success",
        data: result.response.status === "success" ? result.response.data : [],
        error: result.response.status === "error" ? result.response.error ?? null : null,
    };
}

/**
 * Fetches a specific report by ID from the server.
 * 
 * This function performs an API call to retrieve a report based on its unique ID and
 * processes the API response using the `fetchApiData` utility.
 * 
 * @async
 * @function getReport
 * @param {FetchDataByIDProps} params - The request parameters containing a tag and the report's unique ID.
 * @returns {Promise<{ data: ReportContent | null; error: string | null }>} - A promise that resolves to an object containing the report data or an error message.
 * 
 * @example
 * // Example usage:
 * async function loadReportById() {
 *   const result = await getReport({ tag: 'ReportFetch', id: '12345' });
 *   if (result.error) {
 *     console.error('Error fetching report:', result.error);
 *   } else {
 *     console.log('Fetched report:', result.data);
 *   }
 * }
 * // Sample result structure:
 * {
 *   data: {
 *       id: "report1",
 *       title: "2024 Elections in Ghana",
 *       description: "A comprehensive analysis of the upcoming elections.",
 *       dataset: "Politics",
 *       year: "2024",
 *       meta: {
 *           cover: "https://example.com/image.jpg"
 *       }
 *   },
 *   error: null
 * }
 */
export async function getReport({ tag, id }: FetchDataByIDProps): Promise<{ success: boolean; data?: ZeroneReport; error?: string | null }> {
    const result = await fetchApiData<DefaultApiResponse<ZeroneReport>>(
        tag,
        () => get<DefaultApiResponse<ZeroneReport>>({ tag, url: `/report/summary/${id}` }),
        (responseData) => responseData.data ?? null
    );

    return {
        success: result.response.status === "success",
        data: result.response.status === "success" ? result.response.data : null,
        error: result.response.status === "error" ? result.response.error ?? null : null,
    };
}

/**
 * Fetches a list of report catalogs from the server.
 * 
 * This function performs an API call to retrieve report catalogs using the provided tag and
 * processes the API response using the `fetchApiData` utility.
 * 
 * @async
 * @function getReportCatalog
 * @param {EndpointProps} params - The request parameters containing a unique tag for identifying the request.
 * @returns {Promise<{ data: ReportCatalog[]; error: string | null }>} - A promise that resolves to an object containing the array of `ReportCatalog` and any error message.
 * 
 * @example
 * // Example usage:
 * async function loadReportCatalog() {
 *   const result = await getReportCatalog({ tag: 'ReportCatalogFetch' });
 *   if (result.error) {
 *     console.error('Error fetching report catalog:', result.error);
 *   } else {
 *     console.log('Fetched report catalog:', result.data);
 *   }
 * }
 * // Sample result structure:
 * {
 *   data: [
 *       {
 *           report_id: "report1",
 *           dataset: "Politics",
 *           title: "2024 Elections in Ghana",
 *           description: "A comprehensive analysis of the upcoming elections.",
 *           meta: {
 *               cover: "https://example.com/image.jpg"
 *           },
 *       }
 *  ],
 *  error: null
 * }
 * */
export async function getReportCatalog({ tag }: EndpointProps): Promise<{ success: boolean; data?: ReportCatalog[]; error?: string | null}> {
    const result = await fetchApiData<DefaultApiResponse<ReportCatalog[]>>(
        tag,
        () => get<DefaultApiResponse<ReportCatalog[]>>({ tag, url: "/report/catalog" }),
        (responseData) => responseData.data ?? []
    );

    return {
        success: result.response.status === "success",
        data: result.response.status === "success" ? result.response.data : [],
        error: result.response.status === "error" ? result.response.error ?? null : null,
    };
}

/**
 * Fetches the access status of a specific report by ID from the server.
 * 
 * This function performs an API call to check the access status of a report based on its unique ID and
 * processes the API response using the `fetchApiData` utility.
 * 
 * @async
 * @function checkAccessToReport
 * @param {FetchDataByIDProps} params - The request parameters containing a tag and the report's unique ID.
 * @returns {Promise<{ success: boolean; data?: ReportAccess; error?: string | null }>} - A promise that resolves to an object containing the access status or an error message.
 * 
 * @example
 * // Example usage:
 * async function checkReportAccess() {
 *  const result = await checkAccessToReport({ tag: 'ReportAccess', id: '12345' });
 * if (result.error) {
 *    console.error('Error checking report access:', result.error);
 * } else {
 *   console.log('Report access status:', result.data);
 * }
 * }
 * // Sample result structure:
 * {
 *  success: true,
 * data: {
 *   hasAccess: true,
 *  canPurchase: false
 * },
 * error: null
 * }
 * 
 * @example
 * // Sample error structure:
 * {
 * success: false,
 * data: undefined,
 * error: 'Report not found.'
 * }
 * */
export async function checkAccessToReport({ tag, id }: FetchDataByIDProps): Promise<{ success: boolean; data?: ReportAccess; error?: string | null }> {
    const result = await fetchApiData<DefaultApiResponse<ReportAccess>>(
        tag,
        () => get<DefaultApiResponse<ReportAccess>>({ tag, url: `/report/${id}/access` }),
        (responseData) => responseData.data ?? false
    );

    return {
        success: result.response.status === "success",
        data: result.response.status === "success" ? result.response.data : false,
        error: result.response.status === "error" ? result.response.error ?? null : null,
    };
}


/**
 * Downloads a specific report by its ID from the server.
 * 
 * This function performs an API call to download a report based on its unique ID and
 * processes the API response using the `fetchApiData` utility.
 * 
 * @async
 * @function downloadReport
 * @param {FetchDataByIDProps} params - The request parameters containing a tag and the report's unique ID.
 * @returns {Promise<{ success: boolean; data?: { url: string }; error?: string | null }>} - A promise that resolves to an object containing the download URL or an error message.
 * 
 * @example
 * // Example usage:
 * async function downloadReportById() {
 *   const result = await downloadReport({ tag: 'ReportDownload', id: '12345' });
 *   if (result.error) {
 *     console.error('Error downloading report:', result.error);
 *   } else {
 *     console.log('Download URL:', result.data?.url);
 *   }
 * }
 * // Sample result structure:
 * {
 *   success: true,
 *   data: {
 *     url: "https://example.com/download/report12345.pdf"
 *   },
 *   error: null
 * }
 */
export async function downloadReport({ tag, id }: FetchDataByIDProps): Promise<{ success: boolean; data?: { url: string }; error?: string | null }> {
    const result = await fetchApiData<DefaultApiResponse<{ url: string }>>(
        tag,
        () => get<DefaultApiResponse<{ url: string }>>({ tag, url: `/report/${id}/download` }),
        (responseData) => responseData.data ?? null
    );

    return {
        success: result.response.status === "success",
        data: result.response.status === "success" ? result.response.data : null,
        error: result.response.status === "error" ? result.response.error ?? null : null,
    };
}

/**
 * Purchases a specific report using credits from the server.
 * 
 * This function performs an API call to purchase a report using credits based on its unique ID and
 * processes the API response using the `fetchApiData` utility.
 * 
 * @async
 * @function purchaseReportUsingCredits
 * @param {FetchDataByIDProps} params - The request parameters containing a tag and the report's unique ID.
 * @returns {Promise<{ success: boolean; data?: { purchaseSuccessful: boolean; creditsUsed: number; url: string }; error?: string | null }>} - A promise that resolves to an object containing the purchase status, credits used, and download URL or an error message.
 * 
 * @example
 * // Example usage:
 * async function purchaseReportById() {
 *   const result = await purchaseReportUsingCredits({ tag: 'ReportPurchase', id: '12345' });
 *   if (result.error) {
 *     console.error('Error purchasing report:', result.error);
 *   } else {
 *     console.log('Purchase status:', result.data?.purchaseSuccessful);
 *     console.log('Credits used:', result.data?.creditsUsed);
 *     console.log('Download URL:', result.data?.url);
 *   }
 * }
 * // Sample result structure:
 * {
 *   success: true,
 *   data: {
 *     purchaseSuccessful: true,
 *     creditsUsed: 5,
 *     url: "https://example.com/download/report12345.pdf"
 *   },
 *   error: null
 * }
 */
export async function purchaseReportUsingCredits({ tag, id }: FetchDataByIDProps): Promise<{ success: boolean; data?: ReportPurchaseDefaultApiResponse; error?: string | null }> {
    const result = await fetchApiData<DefaultApiResponse<ReportPurchaseDefaultApiResponse>>(
        tag,
        () => get<DefaultApiResponse<ReportPurchaseDefaultApiResponse>>({ tag, url: `/report/${id}/purchase/credits` }),
        (responseData) => responseData.data ?? null
    );

    return {
        success: result.response.status === "success",
        data: result.response.status === "success" ? result.response.data : null,
        error: result.response.status === "error" ? result.response.error ?? null : null,
    };
}

/**
 * Purchases a specific report using payment from the server.
 * 
 * This function performs an API call to purchase a report using payment based on its unique ID and
 * processes the API response using the `fetchApiData` utility.
 * 
 * @async
 * @function purchaseReportUsingPayment
 * @param {FetchDataByIDProps} params - The request parameters containing a tag and the report's unique ID.
 * @returns {Promise<{ success: boolean; data?: ReportPurchaseDefaultApiResponse | StripePaymentIntentResult; error?: string | null }>} - A promise that resolves to an object containing the purchase status, payment intent, or an error message.
 * 
 * @example
 * // Example usage:
 * async function purchaseReportWithPayment() {
 *   const result = await purchaseReportUsingPayment({ tag: 'ReportPurchase', id: '12345' });
 *   if (result.error) {
 *     console.error('Error purchasing report:', result.error);
 *   } else {
 *     console.log('Purchase status:', result.data?.success);
 *     console.log('Payment intent:', result.data?.paymentIntent);
 *   }
 * }
 */
export async function purchaseReportUsingPayment({ tag, id }: FetchDataByIDProps): Promise<{ success: boolean; data?: ReportPurchaseDefaultApiResponse | StripePaymentIntentResult; error?: string | null }> {
    const result = await fetchApiData<DefaultApiResponse<ReportPurchaseDefaultApiResponse | StripePaymentIntentResult>>(
        tag,
        () => get<DefaultApiResponse<ReportPurchaseDefaultApiResponse | StripePaymentIntentResult>>({ tag, url: `/report/${id}/purchase/payment` }),
        (responseData) => responseData.data ?? null
    );

    return {
        success: result.response.status === "success",
        data: result.response.status === "success" ? result.response.data : null,
        error: result.response.status === "error" ? result.response.error ?? null : null,
    };
}

/**
 * Updates the purchase status of a specific report by ID from the server.
 * 
 * This function performs an API call to update the purchase status of a report based on its unique ID and
 * processes the API response using the `fetchApiData` utility.
 * 
 * @async
 * @function updateReportPurchaseStatus
 * @param {FetchDataByIDProps} params - The request parameters containing a tag, the report's unique ID, and the new purchase status.
 * @returns {Promise<{ success: boolean; data?: ReportPurchaseDefaultApiResponse; error?: string | null }>} - A promise that resolves to an object containing the updated purchase status or an error message.
 * 
 * @example
 * // Example usage:
 * async function updateReportPurchase() {
 *   const result = await updateReportPurchaseStatus({ tag: 'ReportUpdate', id: '12345', data: { status: 'purchased' } });
 *   if (result.error) {
 *     console.error('Error updating report purchase:', result.error);
 *   } else {
 *     console.log('Updated purchase status:', result.data?.purchaseSuccessful);
 *   }
 * }
 */
export async function updateReportPurchaseStatus({ tag, id, data }: FetchDataByIDProps & { data: { status: 'paid' | 'failed', paymentId: string } }): Promise<{ success: boolean; data?: ReportPurchaseDefaultApiResponse; error?: string | null }> {
    const result = await fetchApiData<DefaultApiResponse<ReportPurchaseDefaultApiResponse | string>>(
        tag,
        () => post<DefaultApiResponse<ReportPurchaseDefaultApiResponse | string>>({ tag, url: `/report/payment/status/update/${id}`, body: data }),
        (responseData) => responseData.data ?? null
    );

    return {
        success: result.response.status === "success",
        data: result.response.status === "success" ? result.response.data : null,
        error: result.response.status === "error" ? result.response.error ?? null : null,
    };
}

/**
 * Fetches the chart data for a specific report by ID from the server.
 * 
 * This function performs an API call to retrieve the chart data of a report based on its unique ID and
 * processes the API response using the `fetchApiData` utility.
 * 
 * @async
 * @function fetchReportChart
 * @param {FetchDataByIDProps} params - The request parameters containing a tag and the report's unique ID.
 * @returns {Promise<{ success: boolean; data?: LegacyChart; error?: string | null }>} - A promise that resolves to an object containing the chart data or an error message.
 * 
 * @example
 * // Example usage:
 * async function loadReportChart() {
 *   const result = await fetchReportChart({ tag: 'ReportChartFetch', id: '12345' });
 *   if (result.error) {
 *     console.error('Error fetching report chart:', result.error);
 *   } else {
 *     console.log('Fetched report chart:', result.data);
 *   }
 * }
 */
export async function fetchReportChart({ tag, id }: FetchDataByIDProps): Promise<{ success: boolean; data?: LegacyChart; error?: string | null }> {
    const result = await fetchApiData<DefaultApiResponse<LegacyChart>>(
        tag,
        () => get<DefaultApiResponse<LegacyChart>>({ tag, url: `/report/${id}/chart` }),
        (responseData) => responseData.data ?? null
    );

    return {
        success: result.response.status === "success",
        data: result.response.status === "success" ? result.response.data : null,
        error: result.response.status === "error" ? result.response.error ?? null : null,
    };
}

/**
 * Fetches a list of similar reports based on a specific report ID.
 * 
 * This function performs an API call to retrieve similar reports using the provided tag and
 * processes the API response using the `fetchApiData` utility.
 * 
 * @async
 * @function fetchSimilarReports
 * @param {FetchDataByIDProps} params - The request parameters containing a tag and the report's unique ID.
 * @returns {Promise<{ success: boolean; data?: SimilarReport[]; error?: string | null }>} - A promise that resolves to an object containing the array of `SimilarReport` and any error message.
 * 
 * @example
 * // Example usage:
 * async function loadSimilarReports() {
 *   const result = await fetchSimilarReports({ tag: 'SimilarReportsFetch', id: '12345' });
 *   if (result.error) {
 *     console.error('Error fetching similar reports:', result.error);
 *   } else {
 *     console.log('Fetched similar reports:', result.data);
 *   }
 * }
 */
export async function fetchSimilarReports({ tag, id }: FetchDataByIDProps): Promise<{ success: boolean; data?: SimilarReport[]; error?: string | null }> {
    const result = await fetchApiData<DefaultApiResponse<SimilarReport[]>>(
        tag,
        () => get<DefaultApiResponse<SimilarReport[]>>({ tag, url: `/report/${id}/similar` }),
        (responseData) => responseData.data ?? []
    );

    return {
        success: result.response.status === "success",
        data: result.response.status === "success" ? result.response.data : [],
        error: result.response.status === "error" ? result.response.error ?? null : null,
    };
}